import {
  faArrowDown,
  faRightFromBracket
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { Navigate } from "react-router-dom";

import { T } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { LandingBeginButton } from "~/components/kiosk/landing/landing-begin-button";
import { LandingGrid } from "~/components/kiosk/landing/landing-grid";
import { LandingOptionButton } from "~/components/kiosk/landing/landing-option-button";
import { LandingScreen } from "~/components/kiosk/landing/landing-screen";
import { kioskActions } from "~/stores/kiosk-session/actions";
import {
  useKioskDiningOption,
  useKioskSession,
  useKioskSessionStatus
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { Button } from "~/components/kiosk/ui/button/button";
import { type User } from "~/api/users/types";
import { useCurrentSchool } from "~/hooks/queries/schools";
import { getKioskGuestGroupName } from "~/utils/tills/get-kiosk-user-identifier";

import { getDiningOptionsInput } from "../../utils/get-dining-options-input";
import { useKioskStore } from "../__app.kiosk/route";

import { shouldRedirectToProductsPage } from "./should-redirect-to-products-page";

type HeaderProps = {
  user: User | undefined;
};

const WelcomeHeader = ({ user }: HeaderProps) => (
  <h1 className="text-[164px] font-bold leading-none text-kiosk-landing-headline">
    {user ? <T _str="Welcome {name}" name={user.name} /> : <T _str="Ready?" />}
  </h1>
);

const DiningOptionSubheader = ({ user }: HeaderProps) => (
  <div className="text-6xl text-kiosk-landing-subheadline">
    {user ? (
      <T _str="Where will you be eating?" />
    ) : (
      <T _str="Please choose where you will be eating" />
    )}
  </div>
);

const ScanAppButton = () => (
  <Button variant="transparent" className="pointer-events-none border-none">
    <FontAwesomeIcon
      icon={faArrowDown}
      className="text-kiosk-landing-subheadline"
    />
    <p className="font-semibold text-kiosk-landing-subheadline">
      <T _str="Scan your app below" />
    </p>
  </Button>
);

const StartOverButton = () => (
  <Button
    onClick={() => {
      kioskActions.session.reset();
    }}
  >
    <FontAwesomeIcon icon={faRightFromBracket} className="rotate-180" />
    <p className="font-semibold">
      <T _str="Start over" />
    </p>
  </Button>
);

export const KioskLandingPage = () => {
  const preCheckoutTextInputs = useKioskStore(
    (state) => state.till.preCheckoutTextInputs ?? []
  );
  const user = useKioskStore((state) => state.user);
  // enableUserAuthentication it is true even if not set
  const enableUserAuthentication = useKioskStore(
    (state) => state.till.config.enableUserAuthentication !== false
  );
  const till = useKioskStore((state) => state.till);
  const { data: locationGroupNames } = useCurrentSchool(
    (school) => school.groupNames
  );
  // An invoice needs someone to bill, so a billing kiosk only lets identified
  // users start an order, unless guests can order as a guest group (they pay
  // on the terminal).
  const requiresUserAuthentication =
    till.moduleConfig.paymentMethod === "billing" &&
    getKioskGuestGroupName(till, locationGroupNames) === null;

  const status = useKioskSessionStatus();
  const isUserIdentified = useKioskSession((session) =>
    Boolean(session?.childId)
  );
  const diningOption = useKioskDiningOption();

  const { locale } = useCurrentTillLocalisation();

  const diningOptionsInput = getDiningOptionsInput(preCheckoutTextInputs);

  const canStartOrder = !requiresUserAuthentication || isUserIdentified;

  if (
    canStartOrder &&
    shouldRedirectToProductsPage({
      isSessionActive: status === "active",
      isAuthenticated: user !== undefined,
      isDiningOptionSelected: diningOption !== null,
      hasPreCheckoutTextInputs: diningOptionsInput
        ? diningOptionsInput.options.length > 0
        : false
    })
  ) {
    return <Navigate to="/kiosk/products" />;
  }

  if (diningOptionsInput) {
    return (
      <LandingScreen>
        <div className="flex flex-col gap-8">
          <WelcomeHeader user={user} />

          {canStartOrder ? (
            <>
              <DiningOptionSubheader user={user} />

              <LandingGrid itemCount={diningOptionsInput.options.length}>
                {diningOptionsInput.options.map((option: string) => (
                  <LandingOptionButton
                    key={option}
                    className={clsx({
                      "!h-[560px]": diningOptionsInput.options.length === 2
                    })}
                    onClick={() => {
                      if (status === "inactive") {
                        kioskActions.session.start({
                          locale
                        });
                      }

                      kioskActions.session.setDiningOption({
                        inputId: diningOptionsInput.inputId,
                        value: option
                      });
                    }}
                  >
                    {option}
                  </LandingOptionButton>
                ))}
              </LandingGrid>
            </>
          ) : null}

          {user ? <StartOverButton /> : null}
          {!user && enableUserAuthentication ? <ScanAppButton /> : null}
        </div>
      </LandingScreen>
    );
  }

  return (
    <LandingScreen>
      <WelcomeHeader user={user} />

      {canStartOrder ? (
        <LandingBeginButton
          onClick={() => {
            kioskActions.session.start({
              locale
            });
          }}
        />
      ) : null}

      {enableUserAuthentication ? <ScanAppButton /> : null}
    </LandingScreen>
  );
};
